import {
  SUPPORTED_IMAGE_EXTENSIONS,
  SUPPORTED_IMAGE_MIME_TYPES,
} from './constants';

export const MAX_UPLOAD_FILE_SIZE_BYTES = 10 * 1024 * 1024;

export type FileKind = 'image' | 'pdf' | 'csv';

export interface UploadFileValidation {
  isValidFile: boolean;
  isValidSize: boolean;
}

export const isImageFile = (file: File | undefined | null): boolean => {
  if (!file) {
    return false;
  }
  if (SUPPORTED_IMAGE_MIME_TYPES.includes(file.type)) {
    return true;
  }
  // Fallback to extension when the browser omits or misreports MIME.
  const name = file.name.toLowerCase();
  return SUPPORTED_IMAGE_EXTENSIONS.some((ext) => name.endsWith(ext));
};

export const isPdfFile = (file: File | undefined | null): boolean => {
  if (!file) {
    return false;
  }
  if (file.type === 'application/pdf') {
    return true;
  }
  return file.name.toLowerCase().endsWith('.pdf');
};

export const isCsvFile = (file: File | undefined | null): boolean => {
  if (!file) {
    return false;
  }
  if (file.type === 'text/csv' || file.type === 'application/csv') {
    return true;
  }
  // Safari and Excel sometimes send 'application/vnd.ms-excel' or an empty
  // string for CSV files — fall back to extension when MIME is ambiguous.
  return file.name.toLowerCase().endsWith('.csv');
};

export const isSupportedFile = (file: File | undefined | null): boolean =>
  isImageFile(file) || isPdfFile(file) || isCsvFile(file);

export const getFileKind = (file: File): FileKind => {
  if (isImageFile(file)) {
    return 'image';
  }
  if (isPdfFile(file)) {
    return 'pdf';
  }
  return 'csv';
};

// Browsers occasionally hand back an empty `file.type` (notably Safari for
// CSVs, and some image sources). Normalize from the extension so downstream
// code — and the agent — see a predictable MIME.
const EXTENSION_MIME_MAP: Array<[string, string]> = [
  ['.jpg', 'image/jpeg'],
  ['.jpeg', 'image/jpeg'],
  ['.png', 'image/png'],
  ['.webp', 'image/webp'],
  ['.gif', 'image/gif'],
  ['.bmp', 'image/bmp'],
  ['.csv', 'text/csv'],
  ['.pdf', 'application/pdf'],
];

export const inferMimeFromExtension = (file: File): string => {
  if (file.type) {
    return file.type;
  }
  const name = file.name.toLowerCase();
  for (const [ext, mime] of EXTENSION_MIME_MAP) {
    if (name.endsWith(ext)) {
      return mime;
    }
  }
  return 'application/octet-stream';
};

export const getUploadFileValidation = (
  file: File | undefined | null
): UploadFileValidation => {
  if (!file) {
    return { isValidFile: false, isValidSize: false };
  }
  return {
    isValidSize: file.size <= MAX_UPLOAD_FILE_SIZE_BYTES,
    isValidFile: isSupportedFile(file),
  };
};

export const fileToBase64 = (file: File): Promise<string> =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Invalid file data'));
        return;
      }
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });
